import React, { useEffect, useState, useMemo } from "react";
import { productListApiService } from "../../../service/admin/productApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import Pagination from "../../../component/library/pagination";
import ExcelTable from "../../../component/library/excelTable";
import { exportToCSV } from "react-easy-export";
import "./style.css";

// Check if text contains Chinese characters
const isChinese = (text) => /[\u4e00-\u9fff]/.test(text);

// Translation cache to avoid repeated API calls
const translationCache = {};

// Translate Chinese text to English using Google Translate API (free)
const translateToEnglish = async (text) => {
  if (!text || !isChinese(text)) return null;
  if (translationCache[text]) return translationCache[text];
  
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=zh-CN&tl=en&dt=t&q=${encodeURIComponent(text)}`
    );
    const data = await res.json();
    const translated = data?.[0]?.map(item => item[0]).join('') || null;
    if (translated) translationCache[text] = translated;
    return translated;
  } catch (err) {
    return null;
  }
};

// Company Name Cell with translation
const CompanyNameCell = ({ name, onClick }) => {
  const [englishName, setEnglishName] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (name && isChinese(name)) {
      if (translationCache[name]) {
        setEnglishName(translationCache[name]);
      } else {
        setLoading(true);
        translateToEnglish(name).then(translated => {
          setEnglishName(translated);
          setLoading(false);
        });
      }
    }
  }, [name]);

  return (
    <div className="company-cell" onClick={onClick}>
      <span className="company-name-cn">{name || "N/A"}</span>
      {isChinese(name) && (
        <span className="company-name-en">
          {loading ? "Translating..." : englishName || ""}
        </span>
      )}
    </div>
  );
};

export default function SupplierDataInfo() {
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("totalProducts");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const loader = useLoader();
  const message = useMessage();

  const fetchStats = async () => {
    try {
      const res = await productListApiService.getSupplierStats();
      if (res?.status?.code === 200) setStats(res.data);
    } catch (err) {}
  };

  const fetchSuppliers = async () => {
    loader.showInlineLoader("Loading suppliers...", "medium");
    try {
      const queryParams = `?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      const res = await productListApiService.getSuppliersList(queryParams);
      loader.hideLoader();
      setInitialLoading(false);
      if (res?.status?.code === 200) {
        setSuppliers(res.data.suppliers || []);
        setPages({ currentPage: res.data.currentPage, totalPages: res.data.totalPages, totalItems: res.data.totalSuppliers });
      } else {
        message.showToast(res?.error?.message || "Failed to fetch suppliers", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      setInitialLoading(false);
      message.showToast(err?.message || "Server error", ToastTypes.ERROR);
    }
  };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchSuppliers(); }, [page, limit, sortBy, sortOrder]);

  const handleSearch = () => { setPage(1); fetchSuppliers(); };
  
  // Open supplier detail in new tab
  const handleSupplierClick = (supplier) => {
    window.open(`/admin/supplier-data-info/${encodeURIComponent(supplier.sellerOpenId)}`, '_blank');
  };

  const formatNumber = (num) => num ? num.toLocaleString("en-IN") : "0";

  const getScoreBadge = (score) => {
    if (!score) return <span className="score-badge score-na">-</span>;
    const numScore = parseFloat(score);
    let className = "score-na";
    if (numScore >= 4.5) className = "score-excellent";
    else if (numScore >= 4.0) className = "score-good";
    else if (numScore >= 3.5) className = "score-average";
    else className = "score-poor";
    return <span className={`score-badge ${className}`}>{numScore.toFixed(2)}</span>;
  };

  const tableData = useMemo(() => 
    (suppliers || []).map((item, index) => ({
      ...item,
      serialNumber: (page - 1) * limit + index + 1,
    })), [suppliers, page, limit]
  );

  const handleExportCsv = () => {
    const exportData = selectedSuppliers.length > 0 ? selectedSuppliers : tableData;
    if (!exportData.length) { message.showToast("No data to export", ToastTypes.WARNING); return; }
    const csvRows = exportData.map((item) => ({
      "S.No.": item.serialNumber,
      "Company Name": item.companyName || "-",
      "Seller ID": item.sellerOpenId || "-",
      "Trade Score": item.tradeScore?.toFixed(2) || "-",
      "Total Products": item.totalProducts || 0,
      "Actual Sales (₹)": item.actualSalesAmount || 0,
      "Total Orders": item.actualOrderCount || 0,
      "Qty Sold": item.actualQuantitySold || 0,
      "SKUs with Orders": item.skusWithOrdersCount || 0,
      "Verified Products": item.verifiedProducts || 0,
      "Active Products": item.activeProducts || 0,
    }));
    exportToCSV(csvRows, `suppliers_${Date.now()}`);
  };

  const columns = [
    { key: "serialNumber", label: "S.No.", width: 60, render: (v) => <span className="sno-cell">{v}</span> },
    { 
      key: "companyName", 
      label: "Company Name", 
      width: 280,
      render: (v, row) => <CompanyNameCell name={v} onClick={() => handleSupplierClick(row)} />
    },
    { key: "sellerOpenId", label: "Seller ID", width: 150, render: (v) => <span className="seller-id-cell">{v || "-"}</span> },
    { key: "tradeScore", label: "Score", width: 80, render: (v) => getScoreBadge(v) },
    { key: "totalProducts", label: "Products", width: 90, render: (v) => <span className="num-cell">{formatNumber(v)}</span> },
    { key: "actualSalesAmount", label: "Sales", width: 110, render: (v) => <span className="num-cell sales">₹{formatNumber(v || 0)}</span> },
    { key: "actualOrderCount", label: "Orders", width: 80, render: (v) => <span className="num-cell">{formatNumber(v || 0)}</span> },
    { key: "verifiedProducts", label: "Verified", width: 80, render: (v) => <span className="num-cell verified">{formatNumber(v)}</span> },
    { key: "activeProducts", label: "Active", width: 80, render: (v) => <span className="num-cell active">{formatNumber(v)}</span> },
    { key: "actions", label: "", width: 70, render: (_, row) => (
      <button className="view-details-btn" onClick={() => handleSupplierClick(row)}>View</button>
    )}
  ];

  const toolbarLeftActions = (
    <div className="toolbar-left">
      <div className="search-input-wrapper">
        <input type="text" placeholder="Search company or seller ID..." value={search}
          onChange={(e) => setSearch(e.target.value)} onKeyPress={(e) => e.key === "Enter" && handleSearch()} />
        <button onClick={handleSearch}>Search</button>
      </div>
    </div>
  );

  const toolbarRightActions = (
    <div className="toolbar-right">
      <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
        <option value="totalProducts">Products</option>
        <option value="tradeScore">Score</option>
        <option value="actualSalesAmount">Sales</option>
        <option value="actualOrderCount">Orders</option>
      </select>
      <select value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}>
        <option value="desc">↓</option>
        <option value="asc">↑</option>
      </select>
    </div>
  );

  return (
    <div className="supplier-info-container">
      <div className="page-header">
        <span className="header-stats">{formatNumber(stats?.totalSuppliers || pages.totalItems)} suppliers</span>
      </div>

      {stats && (
        <div className="stats-row">
          <div className="stat-box"><span className="stat-num">{formatNumber(stats.totalSuppliers)}</span><span className="stat-lbl">Suppliers</span></div>
          <div className="stat-box"><span className="stat-num">{formatNumber(stats.totalProducts)}</span><span className="stat-lbl">Products</span></div>
          <div className="stat-box"><span className="stat-num">{stats.avgTradeScore || "-"}</span><span className="stat-lbl">Avg Score</span></div>
          <div className="stat-box"><span className="stat-num">₹{formatNumber(stats.totalActualSales || 0)}</span><span className="stat-lbl">Total Sales</span></div>
          <div className="stat-box"><span className="stat-num">{formatNumber(stats.totalOrderItems || 0)}</span><span className="stat-lbl">Orders</span></div>
          <div className="stat-box"><span className="stat-num">{formatNumber(stats.verifiedProducts)}</span><span className="stat-lbl">Verified</span></div>
        </div>
      )}

      <div className="table-section">
        <ExcelTable data={tableData} columns={columns} onExport={handleExportCsv}
          isLoading={initialLoading}
          loadingText="Loading suppliers..."
          showToolbarExport={true} showToolbarFilter={false} showToolbarColumns={true} showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeftActions} toolbarRightActions={toolbarRightActions}
          onSelectionChange={setSelectedSuppliers} selectedData={selectedSuppliers}
          selectable={true} sortable={true} resizable={true} showToolbar={true} title="Suppliers" />
      </div>

      <div className="pagination-section">
        {suppliers.length > 0 && pages.totalPages > 0 ? (
          <Pagination pages={pages} page={page} setPage={setPage} />
        ) : <div className="no-data-msg">No suppliers found</div>}
      </div>
    </div>
  );
}
